/**
 * 채팅 이용 안내(면책) 동의 상태 관리.
 *
 * 원래는 동의 여부를 아무 데도 저장하지 않아 명시적 로그인마다 모달이 다시 떴다.
 * 여기서는 브라우저 로컬에 "언제·어느 버전에 동의했는지"를 남겨 다음 세 경우에만 다시 띄운다.
 *   1) 동의 기록이 없음 (최초 로그인 / 다른 계정 / 브라우저 데이터 삭제)
 *   2) 안내 문구가 개정됨 (RULES_VERSION 불일치)
 *   3) 마지막 동의로부터 REAGREE_DAYS 경과 (주기적 재고지)
 *
 * 서버 저장이 아니므로 기기를 바꾸면 다시 뜬다. 동의 이력을 감사 대상으로 남겨야 한다면
 * PostgreSQL 테이블 + API로 옮겨야 한다(현재 관련 서버 인프라 없음).
 */

/**
 * ChatDisclaimerModal의 안내 문구를 고칠 때마다 올린다 → 전원 즉시 재동의
 *   1: 최초
 *   2: 2026-09 — "감사 및 모니터링" 카드를 "대화 내용의 보호와 열람"으로 교체.
 *      열람 시 본인 통지를 없앴으므로(통지는 최고 관리자에게만) 바뀐 정책을 1회 다시 보여준다.
 */
export const RULES_VERSION = 2;

/** 재동의 주기(일) */
const REAGREE_DAYS = 30;

const storageKey = (userId: string | number) => `disclaimer:${userId}`;

interface DisclaimerRecord {
  /** 동의한 안내 문구 버전 */
  v: number;
  /** 동의 시각 (ISO) */
  at: string;
}

/**
 * 안내 모달을 띄워야 하는지 판정.
 * 저장값이 없거나 손상됐으면 "띄운다"로 폴백한다 (안내를 놓치는 쪽보다 안전).
 */
export function shouldShowDisclaimer(userId: string | number): boolean {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(storageKey(userId));
  } catch {
    return true; // 프라이빗 모드 등 저장소 접근 불가
  }
  if (!raw) return true;

  let record: DisclaimerRecord;
  try {
    record = JSON.parse(raw);
  } catch {
    return true;
  }

  if (record?.v !== RULES_VERSION) return true;

  const agreedAt = new Date(record.at).getTime();
  if (isNaN(agreedAt)) return true;

  const elapsedDays = (Date.now() - agreedAt) / (1000 * 60 * 60 * 24);
  return elapsedDays >= REAGREE_DAYS;
}

/** 동의 기록 저장 (모달의 '동의 후 시작'에서만 호출 — 재열람은 갱신하지 않는다) */
export function recordDisclaimerAgreement(userId: string | number): void {
  const record: DisclaimerRecord = { v: RULES_VERSION, at: new Date().toISOString() };
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(record));
  } catch {
    // 저장 실패는 치명적이지 않다 — 다음 로그인에 다시 뜰 뿐
  }
}
